import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    statusCode?: number;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

export const FLAG_REPORT_ISSUE_TYPES = [
    'sexual_content',
    'violent_or_repulsive_content',
    'hateful_or_abusive_content',
    'harassment_or_bullying',
    'harmful_or_dangerous_acts',
    'suicide_self_harm_or_eating_disorders',
    'misinformation',
    'child_abuse',
    'promotes_terrorism',
    'spam_or_misleading',
    'legal_issue',
    'other',
] as const;

export type AdminFlagReportIssueType = (typeof FLAG_REPORT_ISSUE_TYPES)[number];
export type AdminFlagReportReporterScope = 'all' | 'authenticated' | 'anonymous';
export type AdminFlagReportExamTaskScope = 'all' | 'with' | 'without';

export type AdminFlagReportIssueCount = {
    issueType: string;
    count: number;
};

export type AdminFlagReportSummary = {
    totalReports: number;
    authenticatedReports: number;
    anonymousReports: number;
    reportsWithExamTask: number;
    reportsWithoutExamTask: number;
    issueCounts: AdminFlagReportIssueCount[];
};

export type AdminFlagReportRecord = {
    id: string;
    flagReportId: string;
    userId: string;
    userEmail: string;
    userFullName: string;
    examTaskId: string;
    examName: string;
    examTaskType: string;
    issueType: string;
    reportMessage: string;
    contextPayload: Record<string, unknown>;
    contextPayloadJson: string;
    createdAt: string;
    updatedAt: string;
};

export type AdminFlagReportListResult = {
    page: number;
    pageSize: number;
    totalCount: number;
    items: AdminFlagReportRecord[];
    summary: AdminFlagReportSummary;
};

export type GetAdminFlagReportsParams = {
    page: number;
    pageSize: number;
    search?: string;
    issueType?: string;
    reporterScope?: AdminFlagReportReporterScope;
    examTaskScope?: AdminFlagReportExamTaskScope;
};

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '');
}

function trimSlashes(value: string): string {
    return value.replace(/^\/+|\/+$/g, '');
}

function hasVersionedApiPath(value: string): boolean {
    return /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );

    return { baseUrl, apiVersion, adminToken };
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const candidate = payload as Record<string, unknown>;
        const direct =
            candidate.errorMessage ?? candidate.message ?? candidate.error ?? candidate.title ?? candidate.detail;
        if (typeof direct === 'string' && direct.trim()) return direct;
    }
    return fallback;
}

function unwrapAdminApiPayload<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) {
        return payload as T;
    }

    const envelope = payload as AdminApiEnvelope<T>;

    if (envelope.success === false) {
        throw new Error(getErrorMessage(envelope, 'Admin flag report request failed'));
    }

    if (envelope.data !== undefined) {
        return envelope.data;
    }

    return payload as T;
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : {};
}

function asString(value: unknown, fallback = ''): string {
    return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function parseContextPayload(value: unknown): Record<string, unknown> {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value as Record<string, unknown>;
    }

    if (typeof value === 'string' && value.trim()) {
        try {
            const parsed = JSON.parse(value) as unknown;
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
                ? (parsed as Record<string, unknown>)
                : {};
        } catch {
            return {};
        }
    }

    return {};
}

function normalizeIssueCount(value: unknown): AdminFlagReportIssueCount {
    const record = asRecord(value);

    return {
        issueType: asString(record.issueType),
        count: asNumber(record.count),
    };
}

function normalizeFlagReportRecord(value: unknown): AdminFlagReportRecord {
    const record = asRecord(value);
    const contextPayload = parseContextPayload(record.contextPayload ?? record.contextPayloadJson);

    return {
        id: asString(record.flagReportId),
        flagReportId: asString(record.flagReportId),
        userId: asString(record.userId),
        userEmail: asString(record.userEmail),
        userFullName: asString(record.userFullName),
        examTaskId: asString(record.examTaskId),
        examName: asString(record.examName),
        examTaskType: asString(record.examTaskType),
        issueType: asString(record.issueType),
        reportMessage: asString(record.reportMessage),
        contextPayload,
        contextPayloadJson: JSON.stringify(contextPayload, null, 2),
        createdAt: asString(record.createdAt),
        updatedAt: asString(record.updatedAt),
    };
}

function normalizeFlagReportListResult(value: unknown): AdminFlagReportListResult {
    const record = asRecord(value);
    const summary = asRecord(record.summary);

    return {
        page: asNumber(record.page, 1),
        pageSize: asNumber(record.pageSize, 25),
        totalCount: asNumber(record.totalCount),
        items: Array.isArray(record.items) ? record.items.map(normalizeFlagReportRecord) : [],
        summary: {
            totalReports: asNumber(summary.totalReports),
            authenticatedReports: asNumber(summary.authenticatedReports),
            anonymousReports: asNumber(summary.anonymousReports),
            reportsWithExamTask: asNumber(summary.reportsWithExamTask),
            reportsWithoutExamTask: asNumber(summary.reportsWithoutExamTask),
            issueCounts: Array.isArray(summary.issueCounts) ? summary.issueCounts.map(normalizeIssueCount) : [],
        },
    };
}

function toQueryValue(value: unknown): string {
    if (value === undefined || value === null) return '';
    return String(value).trim();
}

async function requestAdminFlagReport<T>(
    path = '',
    init: RequestInit = {},
    query?: Record<string, string>,
): Promise<T> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const requestPath = trimSlashes(path);
    const url = new URL(requestPath ? `${apiRoot}/AdminFlagReport/${requestPath}` : `${apiRoot}/AdminFlagReport`);

    for (const [key, value] of Object.entries(query ?? {})) {
        if (value !== '') {
            url.searchParams.set(key, value);
        }
    }

    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }
    if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    if (adminToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const response = await fetch(url.toString(), {
        ...init,
        headers,
    });

    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
        const envelope = payload as AdminApiEnvelope<unknown>;
        if (envelope.success === false) {
            throw new Error(getErrorMessage(envelope, 'Admin flag report request failed'));
        }
    }

    return payload as T;
}

export async function getAdminFlagReports(params: GetAdminFlagReportsParams): Promise<AdminFlagReportListResult> {
    const query = {
        page: String(Math.max(1, params.page)),
        pageSize: String(Math.max(1, params.pageSize)),
        search: toQueryValue(params.search ?? ''),
        issueType: toQueryValue(params.issueType ?? ''),
        reporterScope: params.reporterScope && params.reporterScope !== 'all' ? params.reporterScope : '',
        hasExamTask:
            params.examTaskScope === 'with' ? 'true' : params.examTaskScope === 'without' ? 'false' : '',
    };

    const payload = await requestAdminFlagReport<AdminFlagReportListResult | AdminApiEnvelope<AdminFlagReportListResult>>(
        '',
        { method: 'GET' },
        query,
    );

    return normalizeFlagReportListResult(unwrapAdminApiPayload(payload));
}